/**
 * On-chain campaigns for the admin pages. Every campaign Morcast has read from the chain, and the
 * deposits that fund no accepted request, which the owner wallet must void (specification §5.5).
 */

import { morcastEscrowAbi } from "@morcast/protocol";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Address, decodeFunctionData, type Hex } from "viem";
import { useConfig } from "wagmi";

import { api } from "../api/client.js";
import type { Campaign } from "../campaigns/requests.js";
import { callEscrow, EscrowCallError } from "../wallet/escrow.js";

/** A campaign as `GET /api/admin/campaigns` describes it. */
export interface AdminCampaign {
  chainId: number;
  escrow: string;
  /** The escrow's ID of the campaign. IDs restart in every deployment. */
  id: string;
  status: Campaign["status"];
  /** The accepted request the deposit funds, or `null`. */
  requestId: number | null;
  /** Why the deposit funds no request. `null` when it funds one. */
  unmatchedReason: string | null;
  brand: string;
  token: { address: string; symbol: string | null; decimals: number | null };
  /** Base units, as a decimal string. */
  budget: string;
  target: string;
  startAt: string;
  endAt: string;
  manifestHash: string;
  createdTx: string;
  createdAt: string;
  updatedAt: string;
  /**
   * For a deposit to void, the owner's `voidCampaign(id)` call, sent from the owner wallet (`from`)
   * to the escrow with this data and no value. `null` otherwise.
   */
  voidCall: { from: string; to: string; data: string } | null;
}

const ADMIN_CAMPAIGNS_KEY = ["admin-campaigns"] as const;

/**
 * The campaigns of the platform's chain, newest first. With `toVoid`, only the deposits to void.
 */
export function useAdminCampaigns(toVoid: boolean) {
  return useQuery({
    queryKey: [...ADMIN_CAMPAIGNS_KEY, toVoid ? "to-void" : "all"],
    queryFn: async () =>
      (
        await api.get<{ campaigns: AdminCampaign[] }>(
          `/api/admin/campaigns${toVoid ? "?needsVoid=true" : ""}`,
        )
      ).campaigns,
  });
}

/**
 * Voids a deposit from the connected wallet, which must be the escrow's owner. Its whole budget
 * goes back to its brand. It sends the exact call the API describes, after checking that it voids
 * this campaign.
 *
 * @returns The transaction hash. The deposit leaves the list once the worker reads the escrow's
 *          `CampaignVoided` event.
 */
export function useVoidDeposit() {
  const config = useConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (campaign: AdminCampaign) => {
      const voidCall = campaign.voidCall;
      if (!voidCall) {
        throw new EscrowCallError("This deposit does not need voiding.");
      }
      const call = decodeFunctionData({ abi: morcastEscrowAbi, data: voidCall.data as Hex });
      if (
        call.functionName !== "voidCampaign" ||
        voidCall.to.toLowerCase() !== campaign.escrow.toLowerCase() ||
        call.args[0] !== BigInt(campaign.id)
      ) {
        throw new EscrowCallError("This call does not void the deposit.");
      }
      return callEscrow(
        config,
        { chainId: campaign.chainId, escrow: campaign.escrow as Address },
        { functionName: "voidCampaign", args: call.args },
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ADMIN_CAMPAIGNS_KEY }),
  });
}
